import { FormInput } from "@k8slens/lds-form";
import { Tooltip } from "@k8slens/lds";
import { status } from "@k8slens/lds-icons";

import { FormSelect } from "src/components/FormComponents/FormSelect";
import { FormCombobox } from "src/components/FormComponents/FormCombobox";

import type { BillingDetailsForm, FormUpdateParams } from "../types";
import { paymentMethodOptions, countryOptions } from "../utils";

import styles from "./BillingForm.module.css";

const { InfoIcon } = status;

const vatTooltip =
  "A VAT number is a unique identifier used in many countries, including the countries of the European Union, for value-added tax purposes.";
const vatTooltipAustralia =
  "For Australian business we do not collect ABNs. GST will be applied to all purchases as required by law. You can claim GST back through your tax return.";

interface BillingFormProps {
  values: BillingDetailsForm;
  updateValue(params: FormUpdateParams): void;
  showInvoiceMethod?: boolean;
  loading: boolean;
  isAustralia: boolean;
}

export const BillingForm = ({ values, updateValue, loading, isAustralia, showInvoiceMethod }: BillingFormProps) => {
  return (
    <fieldset className={styles.billingForm}>
      <FormSelect
        label="Payment method"
        name="payment-method"
        options={paymentMethodOptions.filter((option) => {
          // Filter out the payment methods that are not available for the business
          if (option.id === "invoice" && !showInvoiceMethod) {
            return false;
          }

          return true;
        })}
        value={paymentMethodOptions.find(({ id }) => id === values.paymentMethod.value)}
        onChange={(value, valid) => updateValue({ key: "paymentMethod", value: value?.id, valid })}
        required={values.paymentMethod.required}
        disabled={loading}
      />
      <div></div>
      <FormInput
        label="First name"
        name="first-name"
        type="text"
        data-recurly="first_name"
        autoComplete="given-name"
        value={values.firstName.value || ""}
        onChange={(value, valid) => updateValue({ key: "firstName", value, valid })}
        required={values.firstName.required}
        disabled={loading}
      />
      <FormInput
        label="Last name"
        name="last-name"
        type="text"
        data-recurly="last_name"
        autoComplete="family-name"
        value={values.lastName.value || ""}
        onChange={(value, valid) => updateValue({ key: "lastName", value, valid })}
        required={values.lastName.required}
        disabled={loading}
      />
      <FormInput
        label="Company name"
        name="company-name"
        type="text"
        autoComplete="organization"
        data-recurly="company"
        value={values.company.value || ""}
        onChange={(value, valid) => updateValue({ key: "company", value, valid })}
        required={values.company.required}
        disabled={loading}
      />
      <FormInput
        label="Billing Email"
        name="email"
        type="email"
        autoComplete="email"
        data-recurly="email"
        value={values.email.value || ""}
        onChange={(value, valid) => updateValue({ key: "email", value, valid })}
        required={values.email.required}
        disabled={loading}
      />
      <FormInput
        label="Address line 1"
        name="street-address"
        type="text"
        autoComplete="address-line1"
        data-recurly="address1"
        value={values.addressStreet1.value || ""}
        onChange={(value, valid) => updateValue({ key: "addressStreet1", value, valid })}
        required={values.addressStreet1.required}
        disabled={loading}
      />
      <FormInput
        label="Address line 2"
        name="street-address-2"
        data-recurly="address2"
        type="text"
        autoComplete="address-line2"
        value={values.addressStreet2.value || ""}
        onChange={(value, valid) => updateValue({ key: "addressStreet2", value, valid })}
        required={values.addressStreet2.required}
        disabled={loading}
      />
      <FormInput
        label="City"
        name="city"
        data-recurly="city"
        type="text"
        autoComplete="address-level2"
        value={values.addressCity.value || ""}
        onChange={(value, valid) => updateValue({ key: "addressCity", value, valid })}
        required={values.addressCity.required}
        disabled={loading}
      />
      <FormInput
        label="State / Province"
        name="region"
        data-recurly="region"
        type="text"
        autoComplete="address-level1"
        value={values.addressRegion.value || ""}
        onChange={(value, valid) => updateValue({ key: "addressRegion", value, valid })}
        required={values.addressRegion.required}
        disabled={loading}
      />
      <FormInput
        label="Postal / Zip code"
        name="postal-code"
        data-recurly="postal_code"
        type="text"
        autoComplete="postal-code"
        value={values.addressPostalCode.value || ""}
        onChange={(value, valid) => updateValue({ key: "addressPostalCode", value, valid })}
        required={values.addressPostalCode.required}
        disabled={loading}
      />
      <FormCombobox
        label="Country"
        id="country-name"
        name="country-name"
        options={countryOptions}
        value={countryOptions.find(({ id }) => id === values.addressCountry.value)}
        onChange={(value, valid) => updateValue({ key: "addressCountry", value: value?.id, valid })}
        required={values.addressCountry.required}
        autoComplete="off"
        translate="no"
        disabled={loading}
      />
      <input type="hidden" name="country-code" data-recurly="country" value={values.addressCountry.value} />
      <FormInput
        labelProps={{
          label: (
            <>
              VAT Number{" "}
              <Tooltip className={styles.tooltip} tabIndex={0} text={isAustralia ? vatTooltipAustralia : vatTooltip}>
                <InfoIcon className={styles.tooltipIcon} />
              </Tooltip>
            </>
          ),
        }}
        label="VAT Number"
        name="vat-number"
        data-recurly="vat_number"
        type="text"
        autoComplete="off"
        value={isAustralia ? "" : values.vatNumber?.value || ""}
        onChange={(value, valid) => updateValue({ key: "vatNumber", value, valid })}
        required={isAustralia ? false : values.vatNumber?.required}
        disabled={isAustralia || loading}
      />
    </fieldset>
  );
};
